import { useCallback, useEffect, useRef } from 'react'
import { useInterviewStore } from '@/store/interviewStore'
import { useJoinStore } from '@/store/joinStore'
import { useLiveKit } from './useLiveKit'
import { useMediaRecorder } from './useMediaRecorder'
import { useTextStream } from './useTextStream'
import type { WordTiming } from './useTextStream'
import { useJoinRealtimeSTT } from './useJoinRealtimeSTT'
import { abortSession, advanceSegment, getQueueStatus } from '@/api/publicSession'
import type { InterviewSegment } from '@/types/interview'
import { buildSegments } from '@/utils/segments'
import { classifyGetUserMediaError } from '@/utils/media'

// Auto-advance once the candidate has been silent (no STT partial) for this
// long. The timer is re-armed on every partial transcript, so a candidate
// who keeps talking — for any length — never triggers it mid-speech.
const SILENCE_TIMEOUT_MS = 20_000
const INTRO_TIMEOUT_MS = 60_000
const OUTRO_LINGER_MS_FALLBACK = 2000
// How long to wait for BEY's avatar video track to attach before opening the
// Start gate anyway (audio-only worker mode, or a dropped video track). Kept
// short so the candidate isn't stuck behind a dead-looking Start button; the
// button also shows an explicit "connecting" state for the whole window.
const AVATAR_VIDEO_FALLBACK_MS = 8_000


// Build the LLM-reaction context strings for a given just-finished segment.
// Mirrors the candidate-flow's getQuestionContext / getNextContext helpers
// in useInterview.ts but operates over the public-flow's resolved.questions
// list (already provided by /v1/public/sessions/{id}).
//
// Note on `nextContext`: intentionally a generic hint, NOT the next
// question's text. The worker LLM prompt says "transition to the next
// topic" + "do not reveal next context". Passing the literal next text
// makes the LLM paraphrase the upcoming question, which then collides
// with the worker speaking that question verbatim right after the
// reaction. Generic hint preserves the prefetch optimisation (via
// `nextTtsText`, which is only used for word-boundary pre-synthesis,
// never injected into the LLM prompt).
function _reactionContext(
  segment: string,
  questions: { text: string }[],
): { questionContext: string; nextContext: string; nextTtsText: string } {
  if (segment === 'intro') {
    const first = questions[0]
    return {
      questionContext: 'Introduction segment',
      nextContext: first
        ? 'Another question is coming next.'
        : 'The interview will conclude after this.',
      nextTtsText: first?.text ?? '',
    }
  }
  if (segment.startsWith('q')) {
    const idx = parseInt(segment.slice(1), 10) - 1
    const current = questions[idx]
    const next = questions[idx + 1]
    return {
      questionContext: current
        ? `Question ${idx + 1}: ${current.text}`
        : '',
      nextContext: next
        ? 'Another question is coming next.'
        : 'The interview will conclude after this.',
      nextTtsText: next?.text ?? '',
    }
  }
  // closing / unknown segment — no follow-up needed.
  return { questionContext: '', nextContext: '', nextTtsText: '' }
}


export function useJoinInterview(sessionId: string) {
  const interviewStore = useInterviewStore()
  const bearer = useJoinStore((s) => s.bearer)
  const livekitConn = useJoinStore((s) => s.livekitConn)
  const resolved = useJoinStore((s) => s.resolved)

  const responseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const speechFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const audioEndTimeRef = useRef<number>(0)
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const advancingRef = useRef(false)
  const startingRef = useRef(false)
  const doneRef = useRef(false)
  const completedRef = useRef(false)
  const stoppedRef = useRef(false)
  const generationRef = useRef(0)
  const setupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingWaitsRef = useRef(new Set<() => void>())
  const isCurrent = useCallback((generation: number) =>
    generationRef.current === generation && !stoppedRef.current, [])
  const beginConnection = useCallback(() => {
    generationRef.current++
    stoppedRef.current = false
    doneRef.current = false
    completedRef.current = false
    startingRef.current = false
    advancingRef.current = false
    useJoinStore.getState().setConnectionId(crypto.randomUUID())
    return generationRef.current
  }, [])
  function wait(ms: number) {
    return new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(timer)
        pendingWaitsRef.current.delete(finish)
        resolve()
      }
      const timer = setTimeout(finish, ms)
      pendingWaitsRef.current.add(finish)
    })
  }
  // True while the segment currently being played is the closing/goodbye.
  // Set synchronously in advance() from the locally-known segment list BEFORE
  // any await, so it is reliable even when the worker's speech_completed for
  // the closing beats the advance() HTTP response (which, under load, it can:
  // the closing TTS is dispatched before start_segment, whose egress setup can
  // delay the response past the short closing playback). onSpeechCompleted
  // must NOT open an answer window after the goodbye — it auto-advances to
  // completion instead. Reading the raced store.currentSegment here let a
  // phantom question appear after the farewell.
  const isPlayingClosingRef = useRef(false)

  const pendingWordTimingsRef = useRef<{ text: string; words: WordTiming[] } | null>(null)
  const speechStartedPendingRef = useRef(false)
  // Resolver for the avatar's reaction-finished signal. Set in advance()
  // before sendStopAndReact, fired by onSpeechCompleted when the reaction's
  // speech_completed arrives. Lets advance wait for the *actual* end of
  // speech rather than the (often too-short) reported durationMs.
  const reactionDoneResolverRef = useRef<(() => void) | null>(null)

  // Synthesize a sessionIdRef that mirrors the sessionId prop so that
  // InterviewScreen (which reads but never mutates it) works identically to
  // the candidate flow where sessionIdRef comes from useRef inside useInterview.
  const sessionIdRef = useRef<string>(sessionId)
  useEffect(() => { sessionIdRef.current = sessionId }, [sessionId])

  // Queue-status polling fallback (issue #22 Phase 2). The worker pushes
  // queue_status via LiveKit data channel every ~2s, but the data channel
  // can drop (especially before /connect, when LiveKit isn't connected at
  // all). Poll the /queue-status endpoint every 5s while the candidate
  // appears to be in the queue, so the overlay stays current either way.
  // Whichever signal updates the store first wins; both read the same
  // Redis state server-side so they agree.
  const queuePosition = useInterviewStore((s) => s.queuePosition)
  useEffect(() => {
    if (queuePosition === null || !bearer || !sessionId) return
    let cancelled = false
    const tick = async () => {
      try {
        const r = await getQueueStatus(sessionId, bearer)
        if (cancelled || stoppedRef.current) return
        if (r.state === 'queued' && typeof r.position === 'number') {
          useInterviewStore.getState().setQueueStatus(
            r.position, r.etaSeconds, r.queueDepth,
          )
        } else {
          // Not queued anymore — clear local state.
          useInterviewStore.getState().setQueueStatus(null, null, null)
        }
      } catch {
        // Best-effort; data channel pushes will recover.
      }
    }
    const id = setInterval(() => { void tick() }, 5000)
    return () => { cancelled = true; clearInterval(id) }
  }, [queuePosition === null, bearer, sessionId])

  const textStream = useTextStream()
  const endInterviewRef = useRef<(aborted?: boolean, options?: { teardown?: boolean }) => Promise<void>>(() => Promise.resolve())
  const advanceSegmentRef = useRef<() => Promise<void>>(() => Promise.resolve())

  function armResponseTimer() {
    if (stoppedRef.current || doneRef.current) return
    const generation = generationRef.current
    if (responseTimerRef.current) clearTimeout(responseTimerRef.current)
    responseTimerRef.current = setTimeout(() => {
      if (isCurrent(generation)) void advanceSegmentRef.current()
    }, SILENCE_TIMEOUT_MS)
  }

  // Reaction-text container. Worker-generated reactions don't have a
  // separate text channel in the public flow — the conversation panel
  // shows them via the same word_timings/textStream path as questions.
  const reactionTextRef = useRef<string>('')

  // Live STT (partial transcript + stop_and_react) — public-bearer endpoint.
  const stt = useJoinRealtimeSTT({
    sessionId,
    onPartialTranscript: (text) => {
      if (stoppedRef.current) return
      interviewStore.setRealtimeTranscript(text)
      // Candidate is still speaking — reset the silence-advance timer so
      // long answers never get cut off mid-sentence.
      if (useInterviewStore.getState().currentPhase === 'user_response') {
        armResponseTimer()
      }
    },
    onFinalTranscript: (_segment, text) => {
      if (!stoppedRef.current) interviewStore.setRealtimeTranscript(text)
    },
    onError: (msg) => console.warn('[stt] error:', msg),
  })

  function onSpeechCompletedImpl() {
    if (doneRef.current || stoppedRef.current) return

    const phaseAtCompleted = useInterviewStore.getState().currentPhase

    // Reaction's speech_completed: snap text, signal advance() that the
    // reaction has actually finished, and bail out. advance waits for this
    // signal (with a fallback timeout) instead of trusting durationMs —
    // the worker reports duration before TTS startup latency is paid, so
    // the timer alone fires too early and races the next segment.
    if (phaseAtCompleted === 'avatar_reacting') {
      textStream.complete()
      if (reactionDoneResolverRef.current) {
        reactionDoneResolverRef.current()
        reactionDoneResolverRef.current = null
      }
      return
    }

    if (speechFallbackTimerRef.current) {
      clearTimeout(speechFallbackTimerRef.current)
      speechFallbackTimerRef.current = null
    }
    textStream.complete()

    // Closing segment just finished — no answer window. Auto-advance so the
    // backend transitions session → completed (fires session.completed
    // webhook) and useJoinInterview.advance triggers endInterview. The ref is
    // authoritative (set before the closing was played); the store check is a
    // fallback for any path that didn't route through advance().
    if (isPlayingClosingRef.current ||
        (useInterviewStore.getState().currentSegment as string) === 'closing') {
      void advanceSegmentRef.current()
      return
    }

    // Open the mic for the candidate's answer. STT might not be connected
    // (offline / WS dropped) — startAudio is a no-op in that case.
    interviewStore.setRealtimeTranscript('')
    stt.startAudio()
    interviewStore.setPhase('user_response')

    armResponseTimer()
  }
  const onSpeechCompletedRef = useRef(onSpeechCompletedImpl)
  const onSpeechCompleted = useCallback(() => onSpeechCompletedRef.current(), [])

  const recorder = useMediaRecorder()

  useEffect(() => { onSpeechCompletedRef.current = onSpeechCompletedImpl })

  const {
    connect: liveKitConnect,
    disconnect: liveKitDisconnect,
    publishStream,
    reattachTracks,
    replaceLocalTrack,
    videoElRef,
    audioElRef,
    waitForAudioEnd,
  } = useLiveKit({
    onAvatarReady: () => {
      if (stoppedRef.current) return
      interviewStore.setStatus('ready')
    },
    onAvatarVideoAttached: () => {
      if (stoppedRef.current) return
      interviewStore.setAvatarReady(true)
    },
    onAvatarUnavailable: (_reason: string) => {
      if (stoppedRef.current) return
      // Worker hit BEY's concurrency limit (or BEY otherwise refused).
      // Block Start, show the candidate the unavailable overlay.
      interviewStore.setAvatarUnavailable(true)
      // Clear any in-progress queue display.
      interviewStore.setQueueStatus(null, null, null)
    },
    onAvatarDegraded: () => {
      if (stoppedRef.current) return
      // BEY persistent 429 — worker is alive in audio-only fallback mode.
      // Show the "continue without avatar" overlay; candidate dismisses
      // it to proceed.
      interviewStore.setAvatarDegraded(true)
      interviewStore.setQueueStatus(null, null, null)
    },
    onQueueStatus: (position: number, etaSeconds: number, queueDepth: number) => {
      if (stoppedRef.current) return
      interviewStore.setQueueStatus(position, etaSeconds, queueDepth)
    },
    onSpeechCompleted,
    onDisconnected: () => {
      if (!stoppedRef.current && useJoinStore.getState().connectionId) void endInterviewRef.current(true)
    },
    onWordTimings: (text, words) => {
      if (stoppedRef.current) return
      pendingWordTimingsRef.current = { text, words }
      // During avatar_reacting, the incoming text IS the reaction the
      // worker just generated. Stash it so the InterviewScreen archive
      // effect picks it up on the avatar_reacting → avatar_speaking
      // transition (see InterviewScreen.tsx:204-210).
      if (useInterviewStore.getState().currentPhase === 'avatar_reacting') {
        reactionTextRef.current = text
      }
      if (speechStartedPendingRef.current) {
        speechStartedPendingRef.current = false
        if (words.length > 0) {
          textStream.startWithTimings(text, words, performance.now())
        }
      }
    },
    onSpeechStarted: () => {
      if (stoppedRef.current) return
      const pending = pendingWordTimingsRef.current
      if (pending && pending.words.length > 0) {
        pendingWordTimingsRef.current = null
        speechStartedPendingRef.current = false
        textStream.startWithTimings(pending.text, pending.words, performance.now())
      } else {
        speechStartedPendingRef.current = true
      }
    },
  })

  // ── Setup ──
  // Optional `overrideConn` lets the caller pass freshly-issued LiveKit
  // credentials directly, sidestepping the closure/render-cycle gap when
  // setLivekitConn() and setup() fire in the same tick.
  const setup = useCallback(async (overrideConn?: { url: string; token: string }, generation = generationRef.current) => {
    if (!isCurrent(generation)) return
    const conn = overrideConn ?? livekitConn
    if (!conn) throw new Error('No LiveKit connection in store')
    if (!resolved) throw new Error('No resolved session in store')

    interviewStore.reset()
    const state = useJoinStore.getState()
    const effectiveLanguage =
      state.selectedLanguage ?? state.resolved?.candidate.availableLanguages[0] ?? 'de'
    interviewStore.setConfig({
      language: effectiveLanguage,
      // Avatar is resolved server-side (candidate /select index -> worker); the
      // public flow sends no gender. This placeholder only satisfies the shared
      // InterviewConfig shape and is not read in this path.
      avatarGender: 'female',
      mode: 'full',
    })
    // Mounts the <video> element in AvatarVideo. Without this the BEY-avatar
    // video track has no element to attach to and stays invisible despite
    // being subscribed.
    interviewStore.setSession(sessionId, conn.url, conn.token, true)
    // Seed the segments list from the resolved template structure so the
    // progress bar denominator matches the actual question count + intro/closing
    // toggles — without this the InterviewScreen falls back to a hardcoded
    // 4-segment list and the progress % wraps mid-interview (issue #21).
    interviewStore.setSegments(buildSegments(
      resolved.questions.length,
      resolved.structure,
    ))
    interviewStore.setStatus('setup')

    await liveKitConnect(conn.url, conn.token)
    if (!isCurrent(generation)) return
    // Promote to 'ready' as soon as the LiveKit room is up. In BEY-avatar mode
    // `onAvatarReady` from useLiveKit also fires when the bey-avatar-agent
    // participant joins — calling setStatus('ready') again is a no-op. In
    // AVATAR_ENABLED=false / audio-only mode the worker joins as
    // 'simulated-agent-*' and `onAvatarReady` never fires, so this line is
    // what unblocks the SetupScreen.
    interviewStore.setStatus('ready')
    // Fallback for the avatar-video-attached signal: if BEY's video never
    // lands within AVATAR_VIDEO_FALLBACK_MS (or the worker is running in
    // audio-only mode), flip avatarReady=true so the Start button isn't gated
    // forever and the loading overlay disappears.
    setupTimerRef.current = setTimeout(() => {
      if (isCurrent(generation) && !useInterviewStore.getState().avatarReady) {
        console.warn('[setup] Avatar video timeout — proceeding without video')
        useInterviewStore.getState().setAvatarReady(true)
      }
    }, AVATAR_VIDEO_FALLBACK_MS)
  }, [liveKitConnect, livekitConn, resolved, interviewStore])

  // ── Start (publish stream + first advance) ──
  const startInterview = useCallback(async () => {
    if (!bearer) throw new Error('No bearer in store')

    // Re-entrancy guard: the Start button stays enabled until status flips
    // to 'running' below, and the getUserMedia prompt can hold that window
    // open for seconds — a second click would leak a second stream/socket.
    if (startingRef.current || stoppedRef.current || doneRef.current) return
    const generation = generationRef.current
    startingRef.current = true
    try {
      interviewStore.setMediaError(null)

      let stream: MediaStream
      try {
        stream = await recorder.startContinuous()
      } catch (err) {
        // Permission denied / no device / device busy: surface a localized
        // banner instead of a button that visibly does nothing. Status stays
        // 'ready' so the candidate can fix the problem and click Start again.
        if (!isCurrent(generation)) return
        console.error('getUserMedia failed:', err)
        interviewStore.setMediaError(classifyGetUserMediaError(err))
        return
      }

      if (!isCurrent(generation)) { stream.getTracks().forEach((track) => track.stop()); return }
      await publishStream(stream)
      if (!isCurrent(generation)) return
      // Open STT WS in parallel — failure is non-fatal (interview still
      // runs without live transcript or reactions).
      try {
        await stt.connect(stream)
      } catch (err) {
        console.warn('[stt] connect failed, continuing without live STT:', err)
      }
      if (!isCurrent(generation)) return
      interviewStore.setStatus('running')
      textStream.reset()
      pendingWordTimingsRef.current = null
      speechStartedPendingRef.current = false
      interviewStore.setPhase('avatar_speaking')

      let elapsed = 0
      elapsedTimerRef.current = setInterval(() => {
        if (!isCurrent(generation)) return
        elapsed++
        interviewStore.setRecordingElapsed(elapsed)
      }, 1000)

      // Trigger the very first segment server-side. Backend sends text to worker
      // via Redis; frontend starts seeing speech_started/word_timings via LiveKit.
      await advanceSegmentRef.current()
    } finally {
      if (generationRef.current === generation) startingRef.current = false
    }
  }, [recorder, publishStream, bearer, interviewStore, stt])

  // ── Advance ──
  const advance = useCallback(async () => {
    if (!bearer || stoppedRef.current || doneRef.current) return
    const generation = generationRef.current
    if (advancingRef.current) return
    advancingRef.current = true
    if (responseTimerRef.current) {
      clearTimeout(responseTimerRef.current)
      responseTimerRef.current = null
    }

    try {
      // If the user is in the answer window, finalize their STT transcript
      // and let the avatar react before pulling the next segment. Worker
      // generates an LLM reaction grounded in the answer; reaction_ready
      // returns the duration so we know when the avatar will be done.
      const phaseBeforeAdvance =
        useInterviewStore.getState().currentPhase
      const segmentBeforeAdvance =
        useInterviewStore.getState().currentSegment
      // Predict, from the locally-seeded segment list, whether the segment
      // we're about to play is the closing — synchronously, before any await,
      // so onSpeechCompleted can never race the backend response and open a
      // phantom answer window after the goodbye. The list mirrors the backend
      // order (intro, q1…qN, closing); currentSegment tracks the last played.
      const segList = useInterviewStore.getState().segments
      const nextSegKey = segList[
        (segmentBeforeAdvance ? segList.indexOf(segmentBeforeAdvance) : -1) + 1
      ]
      isPlayingClosingRef.current = nextSegKey === 'closing'
      if (
        phaseBeforeAdvance === 'user_response' &&
        segmentBeforeAdvance
      ) {
        const ctx = _reactionContext(
          segmentBeforeAdvance,
          resolved?.questions ?? [],
        )
        // Arm the resolver BEFORE setPhase, so a fast speech_completed
        // event from the worker doesn't slip through unhandled.
        const reactionDone = new Promise<void>((resolve) => {
          reactionDoneResolverRef.current = resolve
        })
        textStream.reset()
        pendingWordTimingsRef.current = null
        speechStartedPendingRef.current = false
        interviewStore.setPhase('avatar_reacting')
        try {
          const { durationMs } = await stt.sendStopAndReact(
            segmentBeforeAdvance,
            ctx.questionContext,
            ctx.nextContext,
            ctx.nextTtsText,
          )
          // Wait for the real reaction-end event (with a generous fallback
          // in case it never arrives — durationMs + 5s).
          if (!isCurrent(generation)) return
          await Promise.race([reactionDone, wait(durationMs + 5000)])
          if (!isCurrent(generation)) return
        } catch (err) {
          console.warn('[stt] sendStopAndReact failed:', err)
        } finally {
          if (generationRef.current === generation) reactionDoneResolverRef.current = null
        }
      }

      if (!isCurrent(generation)) return
      // Skip reset when the closing segment just finished — endInterview will handle cleanup
      // and the text should remain visible through the linger period before navigation.
      const isClosing = (useInterviewStore.getState().currentSegment as string) === 'closing'
      if (!isClosing) textStream.reset()
      pendingWordTimingsRef.current = null
      speechStartedPendingRef.current = false
      interviewStore.setPhase('avatar_speaking')
      const connectionId = useJoinStore.getState().connectionId
      if (!connectionId) return
      const res = await advanceSegment(sessionId, bearer, connectionId)
      if (!isCurrent(generation)) return
      // Mirror backend's segment key into the store so the next
      // user_response → advance cycle knows which segment to ack.
      // Cast: backend may emit q3+ (qN) or 'closing' which the static
      // InterviewSegment union doesn't yet include; runtime is fine.
      interviewStore.setSegment(res.currentSegment as InterviewSegment)

      if (res.done) {
        doneRef.current = true
        completedRef.current = true
        await endInterviewRef.current()
      } else {
        // Set fallback timer in case speech_completed never arrives.
        if (speechFallbackTimerRef.current) clearTimeout(speechFallbackTimerRef.current)
        speechFallbackTimerRef.current = setTimeout(() => {
          speechFallbackTimerRef.current = null
          if (isCurrent(generation)) onSpeechCompleted()
        }, INTRO_TIMEOUT_MS)
      }
    } catch (err) {
      if (!isCurrent(generation)) return
      console.warn('advance failed:', err)
      void endInterviewRef.current(true)
    } finally {
      if (generationRef.current === generation) advancingRef.current = false
    }
  }, [bearer, sessionId, interviewStore, onSpeechCompleted, stt])

  useEffect(() => { advanceSegmentRef.current = advance }, [advance])

  // ── End ──
  const endInterview = useCallback(async (aborted = false, { teardown = false }: { teardown?: boolean } = {}) => {
    if (stoppedRef.current && !aborted) return
    const completed = completedRef.current
    stoppedRef.current = true
    doneRef.current = true
    const generation = ++generationRef.current
    if (speechFallbackTimerRef.current) clearTimeout(speechFallbackTimerRef.current)
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current)
    if (responseTimerRef.current) clearTimeout(responseTimerRef.current)
    if (setupTimerRef.current) clearTimeout(setupTimerRef.current)
    pendingWaitsRef.current.forEach((finish) => finish())
    reactionDoneResolverRef.current?.()
    reactionDoneResolverRef.current = null
    const state = useJoinStore.getState()
    if (aborted && !completed && state.bearer && state.connectionId) {
      void abortSession(sessionId, state.bearer, state.connectionId)
        .then((result) => {
          if (generationRef.current === generation && result.status === 'completed') {
            completedRef.current = true
            if (!teardown) interviewStore.setAborted(false)
          }
        })
        .catch((err) => console.warn('Abort notification failed:', err))
    }

    if (!aborted && !teardown) {
      const remainingMs = Math.max(0, audioEndTimeRef.current - performance.now())
      await waitForAudioEnd(remainingMs)
      if (generationRef.current !== generation) return
      interviewStore.setPhase('finalizing')
      await wait(resolved?.postInterviewLingerMs ?? OUTRO_LINGER_MS_FALLBACK)
      if (generationRef.current !== generation) return
    }

    if (!teardown) {
      interviewStore.setAborted(aborted && !completed)
      interviewStore.setStatus('complete')
      interviewStore.setPhase('waiting')
      interviewStore.setQueueStatus(null, null, null)
    }
    try { recorder.stopStream() } catch (err) { console.warn('Media cleanup failed:', err) }
    try { stt.disconnect() } catch (err) { console.warn('STT cleanup failed:', err) }
    try { await liveKitDisconnect() } catch (err) { console.warn('LiveKit cleanup failed:', err) }
  }, [sessionId, liveKitDisconnect, recorder, waitForAudioEnd, interviewStore, stt, resolved])

  useEffect(() => { endInterviewRef.current = endInterview }, [endInterview])

  // ── Unmount safety net ─────────────────────────────────────────────────────
  // endInterview only runs on the happy path. If the candidate navigates away
  // mid-interview (browser back, route change) the layout unmounts with the
  // camera LED on, timers ticking, the LiveKit room connected and the hidden
  // avatar <audio> in document.body still playing. Everything called here is
  // idempotent, so running it after a normal endInterview is harmless.
  useEffect(() => {
    // Leaving releases resources without persisting completion UI state that
    // would redirect a later join in the same document to the thank-you screen.
    const leave = () => { void endInterviewRef.current(true, { teardown: true }) }
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted) window.location.reload()
    }
    window.addEventListener('pagehide', leave)
    window.addEventListener('pageshow', restore)
    return () => {
      window.removeEventListener('pagehide', leave)
      window.removeEventListener('pageshow', restore)
      leave()
    }
  }, [sessionId])

  return {
    beginConnection,
    isCurrent,
    setup,
    startInterview,
    advance,
    advanceSegment: advance,    // alias for the polished InterviewScreen
    endInterview,
    reattachTracks,
    replaceLocalTrack,
    requestPreview: recorder.requestPreview,
    sessionIdRef,
    streamRef: recorder.streamRef,
    videoElRef,
    audioElRef,
    textStream,
    reactionTextRef,
  }
}
